import React, { useContext, createContext, useMemo } from 'react';
import { graphql } from 'react-relay';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { useContentId } from './useContentId.tsx';
import { ALLOWED_CONTENT_STATUSES } from './constants.ts';
import type {
	PageRestrictionsContextQuery$data as PageRestrictionsContextQueryDataType,
	PageRestrictionsContextQuery$variables,
} from './__generated__/PageRestrictionsContextQuery.graphql.ts';

type AncestorType = {
	id: string | null;
	title: string | null;
	hasRestrictions: boolean;
	links: { webui: string | null } | null;
};

export type RestrictionsType = {
	hasInheritedRestrictions: boolean;
	hasViewRestrictions: boolean;
	hasRestrictions: boolean;
	ancestors: AncestorType[];
};

export type PageRestrictionsContextValue = {
	loading: boolean;
	error?: ApolloErrorInterop;
	restrictions?: RestrictionsType;
	refetch: () => void;
};

export type PageRestrictionsContextProviderProps = {
	contentId?: string | Symbol;
	children?: React.ReactNode;
};

const defaultContext: PageRestrictionsContextValue = {
	loading: false,
	refetch: () => {},
};

export const PageRestrictionsContext = createContext<PageRestrictionsContextValue>(defaultContext);

export const PageRestrictionsContextConsumer = PageRestrictionsContext.Consumer;

export function PageRestrictionsContextProvider(props: PageRestrictionsContextProviderProps) {
	const contentId = useContentId(props.contentId);
	const { edition } = useSessionData() || {};
	const includeAncestors = ConfluenceEdition.FREE === edition;

	const { data, loading, error, refetch } = useQueryInterop<
		PageRestrictionsContextQueryDataType,
		PageRestrictionsContextQuery$variables
	>(
		graphql`
			query PageRestrictionsContextQuery(
				$contentId: ID!
				$status: [String]
				$includeAncestors: Boolean!
			) {
				singleContent(id: $contentId, status: $status) {
					id
					hasInheritedRestrictions
					hasViewRestrictions
					hasRestrictions
					ancestors @include(if: $includeAncestors) {
						id
						title
						hasRestrictions
						links {
							webui
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId: contentId?.toString()!,
				status: ALLOWED_CONTENT_STATUSES,
				includeAncestors,
			},
			skip: !contentId || typeof contentId === 'symbol',
		},
	);

	if (error) {
		if (!isUnauthorizedError(error)) {
			getMonitoringClient().submitError(error, { attribution: 'backbone' });
		}
		markErrorAsHandled(error);
	}

	const contextValue = useMemo(
		() =>
			contentId
				? {
						loading,
						error,
						restrictions: transformData(data),
						refetch,
					}
				: defaultContext,
		[loading, error, data, refetch, contentId],
	);

	return (
		<PageRestrictionsContext.Provider value={contextValue}>
			{props.children}
		</PageRestrictionsContext.Provider>
	);
}

const transformData = (
	data?: PageRestrictionsContextQueryDataType,
): RestrictionsType | undefined => {
	const content = data?.singleContent;
	if (content) {
		return {
			hasRestrictions: content.hasRestrictions,
			hasViewRestrictions: content.hasViewRestrictions,
			hasInheritedRestrictions: content.hasInheritedRestrictions,
			ancestors:
				content.ancestors?.map<AncestorType>((a) => ({
					id: a?.id || null,
					title: a?.title || null,
					hasRestrictions: Boolean(a?.hasRestrictions),
					links: { webui: a?.links?.webui || null },
				})) || [],
		};
	}
};

export const usePageRestrictionsContext = () => useContext(PageRestrictionsContext);
